import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { setIsMenuOpen, setSelectedSubItem } from "@/state/menuSlice"
import { gsap } from "gsap"
import { useMemo, useRef } from "react"
import { useIsDesktop } from "@repo/ui/src/providers/DeviceProvider"
import useMenuColor from "@repo/ui/src/hooks/useMenuColor"
import useMenuCurveSvgPaths from "@repo/ui/src/hooks/useMenuCurveSvgPaths"
import useWindowSize from "@repo/ui/src/hooks/useWindowSize"
import { THEME_MODES } from "@repo/ui/themeSettings"
import { CURSOR_TYPES } from "@repo/utils/constants"
import { ToggleMenuButtonSvg1, ToggleMenuButtonSvg2, ToggleMenuButtonSvg3 } from "@repo/ui/SVGR"
import useLogoColorVariable from "../../../../../packages/ui/src/hooks/useLogoColorVariable"
import { useGSAP } from "@repo/utils/gsap"
import { closeMenuAnimation, openMenuAnimation } from "../shared/animations"

const StyledButton = styled.div`
	color: ${({ theme }) => (theme.mode === THEME_MODES.DARK ? theme.colors.white : theme?.colors?.twilightBlue)};
	position: relative;
	z-index: -30;
	transform: translateY(5px);

	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet}px) {
		transform: translateY(14px);
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		transform: translateY(10px);
	}
`

const StyledBounds = styled.div`
	position: absolute;
	left: -40px;
	width: 64px;
	height: 25px;

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		height: 35px;
		cursor: pointer;
		transform: translate(-2px, 0);
		&:hover {
			transform: scaleX(6) scaleY(7);
		}
	}
`

const StyledSingleSvgWrapper = styled.div`
	position: absolute;
	left: ${({ $left }) => `${$left || 0}px`};
	height: auto;
	width: ${({ $width }) => `${$width || 14}px`};
	overflow: hidden;
	translate: 0 -5px;
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		translate: unset;
	}
`

const ToggleMenuButton = () => {
	const dispatch = useDispatch()
	const isDesktop = useIsDesktop()
	const { open: isMenuOpen, isAnimating } = useSelector((state) => state.menu)
	const wrapperRef = useRef(null)
	const tl = useRef(null)
	const iconMenuOpenColor = useMenuColor()
	const iconMenuNotOpenColor = useLogoColorVariable()
	const windowSize = useWindowSize()

	const finalIconColor = useMemo(() => {
		return isMenuOpen ? iconMenuOpenColor : iconMenuNotOpenColor
	}, [iconMenuOpenColor, iconMenuNotOpenColor, isMenuOpen])

	useGSAP(
		() => {
			gsap.set(".toggle-menu-button-svg-1", { x: 0 })
			gsap.set(".toggle-menu-button-svg-2", { x: -5 })
			gsap.set(".toggle-menu-button-svg-3", { x: -9 })
		},
		{ scope: wrapperRef, dependencies: [] },
	)

	useGSAP(
		() => {
			if (!isDesktop) return
			tl.current = gsap.timeline({ paused: true })

			tl.current
				.to(".toggle-menu-button-svg-1", { x: -26, ease: "none", duration: 0.25 })
				.to(".toggle-menu-button-svg-2", { x: -21, ease: "none", duration: 0.25 }, "0")
				.to(".toggle-menu-button-svg-3", { x: -29, ease: "none", duration: 0.25 }, "0")
		},
		{ scope: wrapperRef, dependencies: [isDesktop, windowSize.width] },
	)

	const toggleMenuTl = useRef(null)
	const svgPaths = useMenuCurveSvgPaths()

	const toggleMenu = () => {
		if (isAnimating) return
		let isCloseAnimation = false
		if (isMenuOpen) {
			isCloseAnimation = true
			dispatch(setIsMenuOpen(false))
			dispatch(setSelectedSubItem(null))
			toggleMenuTl.current = closeMenuAnimation(svgPaths, dispatch)
		} else {
			toggleMenuTl.current = openMenuAnimation(svgPaths, dispatch)
			dispatch(setIsMenuOpen(true))
		}

		toggleMenuTl.current.timeScale(isCloseAnimation ? 1.4 : 1).restart()
	}

	return (
		<div style={{ position: "relative" }}>
			<StyledBounds
				data-cursor={CURSOR_TYPES.STICKY}
				onClick={toggleMenu}
				className="toggle-menu-button"
				onMouseEnter={() => isDesktop && tl.current.play()}
				onMouseLeave={() => isDesktop && tl.current.reverse()}
				ref={wrapperRef}
			/>
			<StyledButton className="sticky-button-element">
				<StyledSingleSvgWrapper $left={-40}>
					<ToggleMenuButtonSvg1 color={finalIconColor} style={{ transition: "all .3s" }} />
				</StyledSingleSvgWrapper>
				<StyledSingleSvgWrapper $left={-20}>
					<ToggleMenuButtonSvg2 color={finalIconColor} style={{ transition: "all .3s" }} />
				</StyledSingleSvgWrapper>
				<StyledSingleSvgWrapper $width={11}>
					<ToggleMenuButtonSvg3 color={finalIconColor} style={{ transition: "all .3s" }} />
				</StyledSingleSvgWrapper>
			</StyledButton>
		</div>
	)
}

export default ToggleMenuButton
